import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { BILLING_PRESETS, DEFAULT_BILLING_FORMULA, validateBillingFormula, evalBillingFormula } from '../utils/billingFormula';

// Self-contained editor for the company-wide billing formula (amount = f(rate, meter, avgPick, piece)).
// Loads the current value from /admin-settings, lets an admin pick a preset or type a custom equation,
// previews the result, and saves. Reused wherever the billing rule is configured (Settings, Challan gear).
const BillingFormulaEditor: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: settings } = useQuery<any>({
    queryKey: ['adminSettings'],
    queryFn: async () => (await apiService.get('/admin-settings')).settings,
    staleTime: 5 * 60 * 1000,
  });

  const [formula, setFormula] = useState<string>(DEFAULT_BILLING_FORMULA);
  const [custom, setCustom] = useState<boolean>(false);

  useEffect(() => {
    const f = settings?.billingFormula;
    if (f) { setFormula(f); setCustom(!BILLING_PRESETS.some((p) => p.formula === f)); }
  }, [settings]);

  const save = useMutation({
    mutationFn: async (f: string) => apiService.put('/admin-settings', { billingFormula: f }),
    onSuccess: () => {
      // Refresh both cache keys the app uses for admin settings so every page picks up the change.
      queryClient.invalidateQueries({ queryKey: ['adminSettings'] });
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      toast.success('Billing formula saved');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to save billing formula'),
  });

  const error = validateBillingFormula(formula);
  const preview = evalBillingFormula(formula, { rate: 10, meter: 100, avgPick: 48, piece: 1 });
  const selectValue = custom ? 'custom' : formula;

  return (
    <div className="space-y-2">
      <select
        value={selectValue}
        onChange={(e) => {
          if (e.target.value === 'custom') setCustom(true);
          else { setCustom(false); setFormula(e.target.value); }
        }}
        className="w-full px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]"
      >
        {BILLING_PRESETS.map((p) => <option key={p.formula} value={p.formula}>{p.label}</option>)}
        <option value="custom">Custom formula…</option>
      </select>
      {custom && (
        <input
          type="text"
          value={formula}
          onChange={(e) => setFormula(e.target.value)}
          placeholder="e.g. rate * meter * 1.05"
          className={`w-full px-2.5 py-1.5 text-sm font-mono rounded-lg focus:outline-none focus:ring-2 ${error ? 'border border-red-300 focus:ring-red-500/20' : 'border border-[#E1E6EF] focus:ring-[#0055a9]/20 focus:border-[#0055a9]'}`}
        />
      )}
      {error ? (
        <p className="text-xs text-red-600">{error}</p>
      ) : (
        <p className="text-xs text-[rgba(14,19,27,0.5)]">
          Vars: rate, meter, avgPick, piece · Sample →{' '}
          <span className="font-semibold text-[#0E131B]">{preview.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </p>
      )}
      <button
        onClick={() => { if (!error) save.mutate(formula.trim()); }}
        disabled={save.isPending || !!error}
        className="w-full px-3 py-1.5 text-sm font-semibold text-white bg-[#0055a9] rounded-lg hover:bg-[#0d2780] disabled:opacity-50 transition-colors"
      >
        {save.isPending ? 'Saving…' : 'Save Billing Formula'}
      </button>
    </div>
  );
};

export default BillingFormulaEditor;
